import { AlertVariant, Button, Form, FormGroup, TextInput } from '@patternfly/react-core';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { TFolder } from '../../../api/foundational-elements/Folder';
import { DraggableMenuItemData } from '../../../types/databuilder/databuilder';
import { TNewDateRange } from '../../../api/types/TNewDateRange';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import EntityMiscButtons from '../../../helpers/helper-components/EntityMiscButtons';
import { MultipartResponse } from '../../../helpers/multipart-response.helper';
import { DataframeDataRetrievalRequest, DataframeDataRetrievalResponse } from '../../../api/types';
import DatePeriodSelector from '../../../components/date-period-selector/DatePeriodSelector';
import FilterModal from '../../../components/modals/databuilder/FilterModal';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import { Favorites, TFavorites } from '../../../api/favorites/Favorites';
import { FavoriteTypes } from '../../../enums/favorite-types';
import { useToast } from '@zeroedin-tech/zi-common-ui/lib';
import { useMount } from 'react-use';
import UserSecureFiltersIcon from '../../../components/user-secure-filters-icon/UserSecureFiltersIcon';
import { useNavigate, useParams } from 'react-router-dom';

type Props = {
	name?: string;
	folders: TFolder[];
	folder?: TFolder;
	onRunReport: () => void;
	parameters: DraggableMenuItemData[];
	setParameters: Dispatch<SetStateAction<DraggableMenuItemData[]>>;
	setSelectedDate: Dispatch<SetStateAction<TNewDateRange | undefined>>;
	previewData?: MultipartResponse<DataframeDataRetrievalResponse>;
	isTable?: boolean;
	reportId: number;
	userId: number;
	sharedPermissions: { canEdit: boolean; canShare: boolean };
	isColTotalsChecked?: boolean;
	setIsColTotalsChecked?: React.Dispatch<React.SetStateAction<boolean>>;
	isRowTotalsChecked?: boolean;
	setIsRowTotalsChecked?: React.Dispatch<React.SetStateAction<boolean>>;
	dataframeId: number;
	selectedDate: TNewDateRange | undefined;
	exportRequest?: DataframeDataRetrievalRequest;
};

const ViewReport = (props: Props) => {
	const {
		name,
		folders,
		folder,
		onRunReport,
		parameters,
		setParameters,
		setSelectedDate,
		previewData,
		isTable,
		reportId,
		userId,
	} = props;

	const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
	const [activeFilter, setActiveFilter] = useState<DraggableMenuItemData>();
	const [favorite, setFavorite] = useState<TFavorites>();
	const { addToast } = useToast();
	const navigate = useNavigate();
	// Only the dashboard-nested routes carry this param, and the widget link falls back to 0
	// when a table is rendered outside a dashboard - so read it as a number, not a truthy string.
	const { dashboardId } = useParams();
	const originDashboardId = Number(dashboardId ?? 0);
	const isDashboardWidgetView = originDashboardId > 0;

	useMount(() => {
		getFavoriteData();
	});

	const handleFilterModalOpen = (param: DraggableMenuItemData) => {
		setActiveFilter(param);
		setIsFilterModalOpen(true);
	};

	const handleFilterModalSave = (newParam: DraggableMenuItemData) => {
		setParameters(
			parameters.map((param) => {
				if (
					param.entityType === newParam.entityType &&
					param.data?.id === newParam.data?.id
				) {
					if (param.data) {
						param.data.showSetFilterIcon = true;
					}
					param = newParam;
				}
				return param;
			})
		);
		setIsFilterModalOpen(false);
	};

	const handleFilterModalClose = () => {
		setIsFilterModalOpen(false);
	};

	const updateSelectedDate = (startDateRange: TNewDateRange, endDateRange: TNewDateRange) => {
		if (setSelectedDate) {
			setSelectedDate({
				begin_date: startDateRange.begin_date,
				end_date: endDateRange.end_date,
				period: startDateRange.period,
				sequence: startDateRange.sequence,
			});
		}
	};

	const getFavoriteData = () => {
		Favorites.Get(+(reportId ?? 0), props.isTable ? FavoriteTypes.table : FavoriteTypes.report)
			.then((response: TFavorites) => {
				if (response) {
					setFavorite(response);
				} else {
					setFavorite({
						owner: userId,
						entity_id: +(reportId ?? 0),
						entity_type: props.isTable ? FavoriteTypes.table : FavoriteTypes.report,
						name: name ?? '',
					});
				}
			})
			.catch(() => {
				addToast(
					'There was an issue setting the report as a favorite',
					AlertVariant.danger
				);
			});
	};

	return (
		<div className={'view-report-container'}>
			<div className={'details-container'}>
				<div className={'details'}>
					<div className={'form-container'}>
						<Form>
							<FormGroup
								label={
									<div className="d-flex align-items-center">
										<div>{`${!isTable ? 'Report' : 'Table'} Name`}</div>
										<UserSecureFiltersIcon />
									</div>
								}
								type="text"
								fieldId="name"
							>
								<TextInput
									type="text"
									value={name}
									isDisabled={true}
								/>
							</FormGroup>
						</Form>
					</div>
					{(!isTable || isDashboardWidgetView) && (
						<div className={'actions-container'}>
							{!isTable && (
								<Button
									variant="primary"
									onClick={onRunReport}
								>
									Run Report
								</Button>
							)}
							{isDashboardWidgetView && (
								<Button
									variant="primary"
									onClick={() => {
										navigate(`/analyze/dashboards/view/${originDashboardId}`);
									}}
								>
									Return to Dashboard
								</Button>
							)}
						</div>
					)}
				</div>
			</div>
			<div className={'misc-actions'}>
				<EntityMiscButtons
					entityType={!isTable ? 'Report' : 'Table'}
					entityName={name ?? ''}
					canShare={props.sharedPermissions.canShare}
					canEdit={props.sharedPermissions.canEdit}
					exportData={previewData}
					limitReached={previewData?.json?.result_size?.limitReached}
					favorite={favorite}
					setFavorite={getFavoriteData}
					isView={true}
					isColTotalsChecked={props.isColTotalsChecked}
					setIsColTotalsChecked={props.setIsColTotalsChecked}
					isRowTotalsChecked={props.isRowTotalsChecked}
					setIsRowTotalsChecked={props.setIsRowTotalsChecked}
					dataframeId={props.dataframeId}
					selectedDate={props.selectedDate}
					exportRequest={props.exportRequest}
				/>
			</div>
			<div className={'filters'}>
				<Form isHorizontal>
					<FormGroup
						label="Filters"
						isInline
					>
						<div className="date-selector-container">
							<DatePeriodSelector applyDateRange={updateSelectedDate} />
						</div>

						{parameters.map((param) => {
							return (
								<>
									<div className={`custom-button-chip`}>
										<div
											className="label"
											onClick={() => {
												handleFilterModalOpen(param);
											}}
										>
											{param.data?.title}
										</div>
										{param.data?.showSetFilterIcon && (
											<>
												<FontAwesomeIcon icon={faFilter} />
												<>&nbsp;</>
											</>
										)}
									</div>
								</>
							);
						})}
					</FormGroup>
				</Form>
			</div>
			{isFilterModalOpen && (
				<FilterModal
					filter={activeFilter ?? null}
					handleClose={handleFilterModalClose}
					handleSave={handleFilterModalSave}
					readonly={activeFilter?.data?.disabled}
				/>
			)}
		</div>
	);
};

export default ViewReport;
